import { Button } from '@design-system/button';
import type { FC } from 'react';
import type { TPlan, TPlanApi } from '@shared/types/plan';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system/tooltip';
import PricingListItem from '@features/settings/pages/billing/pricing/components/pricing-list-item';
import useCheckSubscription from '@shared/hooks/use-check-subscription';
import { Dialog, DialogContent, DialogTrigger } from '@design-system/dialog';
import CancelSubscriptionDialog from './cancel-subscription-dialog';

const MockedPriceCard: FC<{
  plan: TPlan;
  yearly?: boolean;
  handleSubscribe?: (id: number) => void;
  selectedPlanId?: number;
  plans?: TPlanApi[];
}> = ({ plan, yearly, selectedPlanId, handleSubscribe, plans }) => {
  const { features } = plan;
  const { isFreeTrial: freeTrial, isTrialCompleted } = useCheckSubscription();
  const planId = plans?.find((p) => p.name === plan.title)?.id;

  return (
    <div className='relative mt-7 h-full'>
      <div className='badge hidden font-normal text-base text-white'>
        Best Value
      </div>
      <div
        className={`w-full h-full flex flex-col gap-4 bg-white rounded-xl py-8 px-4 border border-primary-200 text-center rel`}
      >
        <h2 className='text-2xl font-bold text-primary-1000 text-center uppercase'>
          {plan.title}
        </h2>
        <p className='text-gray-800 text-base font-normal mb-5 h-9'>
          {plan.description}
        </p>
        <div>
          {yearly && (
            <p className='text-gray-600 text-base font-normal mb-0 line-through'>
              ${plan.strikePrice}
            </p>
          )}
          <p className='text-gray-800 font-semibold text-5xl'>
            <span className='text-gray-600 font-normal text-lg'>$</span>
            {yearly ? plan.yearlyPrice : plan.price}
          </p>
        </div>
        <p className='text-gray-600 font-normal text-sm flex justify-center gap-2'>
          <span>Price/Org/Month Billed Monthly</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='rounded-full border-gray-600 border w-5 h-5 cursor-pointer'>
                ?
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <span className='text-gray-900 text-sm font-medium'>
                Pay {yearly ? 'Upfront' : 'Monthly'} - Commit Annually
              </span>
            </TooltipContent>
          </Tooltip>
        </p>
        <div className='flex flex-col items-center mt-3'>
          {plan.saves && yearly && (
            <div className='border w-11/12 bg-secondary-100 text-secondary-1000 border-secondary-200 border-b-0 rounded-b-none px-3 py-1.5 rounded-lg'>
              Save ${plan.saves}
            </div>
          )}
          {planId === selectedPlanId && !freeTrial && !isTrialCompleted ? (
            <Button
              disabled
              variant='ghost'
              className='w-full bg-primary-50 linear text-primary-1000 opacity-100'
            >
              My Current Plan
            </Button>
          ) : !freeTrial ? (
            <Dialog>
              {isTrialCompleted ? (
                <Button
                  variant='ghost'
                  onClick={() => handleSubscribe?.(planId || 0)}
                  className='w-full border-primary-1000 linear border text-primary-1000'
                >
                  Subscribe
                </Button>
              ) : (
                <DialogTrigger className='w-full '>
                  <Button
                    variant='ghost'
                    className='w-full border-primary-1000 linear border text-primary-1000'
                  >
                    Subscribe
                  </Button>
                </DialogTrigger>
              )}
              <DialogContent align='center'>
                <CancelSubscriptionDialog
                  onSuccess={() => handleSubscribe?.(planId || 0)}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <Button
              variant='ghost'
              className='w-full border-primary-1000 linear border text-primary-1000'
              onClick={() => handleSubscribe?.(planId || 0)}
            >
              Start my free trial
            </Button>
          )}
          <p className='text-gray-600 text-xs font-normal mt-2'>
            No Credit Card Needed
          </p>
        </div>
        <ul className='list-disc pl-5 text-gray-800 text-start mt-4 font-normal'>
          <PricingListItem content={`${features.users} Users`} />
          {/* <PricingListItem
            content={`${features.accounts ? features.accounts : ''} Accountants`}
            disabled={!features.accounts}
          /> */}
          <PricingListItem content={`${features.applications} Applications`} />
          <PricingListItem
            content={`${features.programs} Programs (${features.programsBand})`}
          />
          <PricingListItem content={`${features.placements} Placements`} />
          <PricingListItem content={`${features.leads} Leads`} />
          <PricingListItem
            content={`${features.invoices ? features.invoices : ''} Invoices`}
            disabled={!features.invoices}
          />
          <PricingListItem
            content={features.tasks ? `Tasks (${features.tasks})` : 'Tasks'}
            disabled={!features.tasks}
          />
          <PricingListItem
            content={`Online payment ${plan.id == 3 ? '(Add-on $399 Annual)' : ''}`}
            disabled={!features.onlinePayments}
          />
          <PricingListItem
            content={`${features.whatsappMessages ? features.whatsappMessages : ''} Whatsapp Messages`}
            disabled={!features.whatsappMessages}
          />
          <PricingListItem
            content={`${features.smsMessages ? features.smsMessages : ''} SMS`}
            disabled={!features.smsMessages}
          />
          <PricingListItem content={`${features.emails} Emails`} />
          <PricingListItem
            content={`${features.appointments ? features.appointments + ' Active' : ''} Appointments`}
            disabled={!features.appointments}
          />
          <PricingListItem
            content={`${features.bulkReminders ? features.bulkReminders : ''} Bulk Reminders`}
            disabled={!features.bulkReminders}
          />
          <PricingListItem content={`${features.storage} Storage`} />
          <PricingListItem
            content={`${features.yearsArchiving} Years Archiving`}
          />
          <PricingListItem
            content={`Student Portal (${features.studentPortalBand})`}
          />
          <PricingListItem
            content={`Reminders Mng (${features.remindersMngBand})`}
          />
          <PricingListItem
            content={`Partners Management`}
            disabled={!features.partnersManagementBand}
          />
          <PricingListItem
            content={`(${features.activityLogs}) Days of Activity Logs`}
          />
          <PricingListItem
            content={`Multi Organization`}
            disabled={!features.multiOrganization}
          />
          <PricingListItem
            content={`${features.integration ? features.integration : ''} Integration`}
            disabled={!features.integration}
          />
          <PricingListItem
            content={`${features.managementBand} Management`}
            lastItem
            basic={plan.id === 1}
          />
        </ul>
      </div>
    </div>
  );
};

export default MockedPriceCard;
